import { Client, createRequest } from 'urql';
import { filter, pipe, take, toPromise } from 'wonka';
import { GET_LOCATIONS } from 'src/graphql/requests/getLocations';
import { GCustomerLocationsInput } from 'src/graphql/types/customerLocationFilters.generated';
import { GetLocation } from 'src/graphql/types/delivery';
import { GGetLocationsQuery } from 'src/graphql/types/generated';
import { isDefined } from 'src/utils';

// The backend caps a customerLocations page at 100 rows.
export const LOCATIONS_PAGE_SIZE = 100;

export type GetLocationsVariables = {
  input: GCustomerLocationsInput;
  first: number;
  after?: string;
};

// Pages through every location matching `input`. Used on demand only — select-all, CSV
// export and the bulk-edit handoff — so the table itself never loads the whole warehouse.
export const fetchAllCustomerLocations = async (
  client: Client,
  input: GCustomerLocationsInput,
): Promise<GetLocation[]> => {
  const locations: GetLocation[] = [];
  let after: string | undefined;
  for (;;) {
    const variables: GetLocationsVariables = {
      input,
      first: LOCATIONS_PAGE_SIZE,
      after,
    };
    // Pages depend on the previous cursor, so they are fetched one after another.
    // eslint-disable-next-line no-await-in-loop
    const result = await pipe(
      client.executeQuery(createRequest(GET_LOCATIONS, variables), {
        requestPolicy: 'network-only',
      }),
      filter((operationResult) => !operationResult.stale),
      take(1),
      toPromise,
    );
    if (result.error) throw result.error;
    const connection = (result.data as GGetLocationsQuery | undefined)
      ?.customerLocations;
    if (!connection) throw new Error('Locations could not be loaded.');
    locations.push(...(connection.nodes ?? []).filter(isDefined));
    const { hasNextPage, endCursor } = connection.pageInfo;
    if (!hasNextPage || !endCursor) return locations;
    after = endCursor;
  }
};
